import { IntelligenceFixture } from '../../types';
import { PLACEHOLDER_WHY_THIS_RESULT, PLACEHOLDER_CONFIDENCE_REASON } from '../placeholderCopy';
import { FIXTURE_UPDATED_AT } from '../updatedAt';

/* DEWS names six factors and raises on every one of them, on a score a rise in is bad news, so each
   is a blocker; Habit Strength's four terms all raise a score a rise in is good. Both directions are
   the registry's, read off each metric's `governed_formula_expression`. PLA-8815. */
const BLOCKS = 'blocker' as const;
const DRIVES = 'positive' as const;

export const behaviourEngagementCardFixtures: Record<string, IntelligenceFixture> = {
  // Node 589-30391, frame 589:27692 — the four scores the screen prints, in its own left-to-right
  // order (design/card-customer-behaviour-trends-devmode-2026-08-12.md § 3). The card used to report
  // transaction frequency and basket size; neither string appears on either frame's copy of it, and
  // the card's own subtitle names this family instead.
  customer_behaviour_trends: {
    cardKey: 'customer_behaviour_trends',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    // Labels are the screen's own, respelled British: Figma writes `Behavioral`, while the tooltip
    // PDF that specifies this family writes `Behavioural`, as does every other label we ship.
    // Every delta is the screen's `↑ 2.0 pts`, identical on all four tiles — a copy-paste tell in the
    // file, reproduced rather than invented around, and raised with the designer.
    // `sentiment` is NOT a screen reading: no fill is exposed on any of the four value layers. It
    // comes from the PDF's own `Read` sentence — momentum up is improving, disengagement up is
    // weakening, and high deviation is explicitly "not necessarily negative", so it stays neutral.
    metrics: [
      {
        key: 'behavioural_momentum',
        label: 'Behavioural Momentum Score (BMS)',
        value: 68,
        format: 'points',
        delta: { value: 2, unit: 'points', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'behavioural_deviation',
        label: 'Behavioural Deviation Score (BDS)',
        value: 42,
        format: 'points',
        delta: { value: 2, unit: 'points', direction: 'up', sentiment: 'neutral' }
      },
      // Both scores carry the factor sets their own questions answer with, so the fixture screen
      // draws the two evidence rails the live screen now draws. The means are UAT `generic`'s own
      // (2026-09-08) rather than authored: the design gave three figures for a six-factor score and
      // categorical words for the other rail, neither of which a live response can produce.
      // Strongest first, which is the order `assembleCard` ranks them in. PLA-8815.
      {
        key: 'disengagement_early_warning',
        label: 'Disengagement Early Warning Score',
        value: 31,
        format: 'points',
        delta: { value: 2, unit: 'points', direction: 'up', sentiment: 'negative' },
        factors: [
          {
            key: 'dews_mean_negative_sentiment',
            label: 'Sentiment risk',
            value: 0.5,
            tone: BLOCKS
          },
          { key: 'dews_mean_recency_drop', label: 'Recency drop', value: 0.43196, tone: BLOCKS },
          {
            key: 'dews_mean_frequency_drop',
            label: 'Frequency drop',
            value: 0.383961,
            tone: BLOCKS
          },
          {
            key: 'dews_mean_engagement_drop',
            label: 'Engagement drop',
            value: 0.224614,
            tone: BLOCKS
          },
          {
            key: 'dews_mean_expectation_gap',
            label: 'Expectation gap',
            value: 0.170362,
            tone: BLOCKS
          },
          {
            key: 'dews_mean_tenure_adjustment',
            label: 'Tenure adjustment',
            value: 0.0057,
            tone: BLOCKS
          }
        ]
      },
      {
        key: 'habit_strength',
        label: 'Habit Strength Score',
        value: 74,
        format: 'points',
        delta: { value: 2, unit: 'points', direction: 'up', sentiment: 'positive' },
        factors: [
          { key: 'hss_mean_tenure_norm', label: 'Tenure norm', value: 0.8888, tone: DRIVES },
          {
            key: 'hss_mean_repeated_percentile',
            label: 'Repeated-event count',
            value: 0.440941,
            tone: DRIVES
          },
          {
            key: 'hss_mean_periodicity',
            label: 'Periodicity consistency',
            value: 0.439671,
            tone: DRIVES
          },
          {
            key: 'hss_mean_interval_consistency',
            label: 'Interval variance inverse',
            value: 0.223317,
            tone: DRIVES
          }
        ]
      }
    ],
    // The screen draws both the badge and the `Reason:` line under it, and the reason is still Lorem
    // Ipsum in the file — so the elements ship carrying the shared placeholder, not invented prose.
    confidence: { score: 83, band: 'high', reasons: [PLACEHOLDER_CONFIDENCE_REASON] },
    whyThisResult: PLACEHOLDER_WHY_THIS_RESULT
  },

  customer_engagement: {
    cardKey: 'customer_engagement',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    breakdownKey: 'multi_source_coverage',
    // Figures, labels and the delta are node 589-30494's own
    // (design/card-customer-engagement-devmode-2026-08-12.md § 3.2). They close on themselves and on
    // the rest of the screen: 46,915 engaged ÷ 64,800 eligible = the 72.4% the ring prints, and
    // 64,800 is already Customer Population & Activity's eligible figure (customerBase.ts:204).
    metrics: [
      {
        key: 'eligible_customers',
        label: 'Eligible Customers',
        value: 64800,
        format: 'count',
        // Both tiles carry the screen's identical movement, verbatim. Its `(0.2% pp)` reconciles
        // against neither count on either basis, which is why it is a `pp` secondary and not a
        // `percent` one: the unit states the form the screen prints rather than recomputing it into
        // something the screen does not show.
        delta: {
          value: 1250,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 0.2, unit: 'pp' }
        }
      },
      {
        key: 'engaged_customers',
        label: 'Engaged Customers',
        value: 46915,
        format: 'count',
        delta: {
          value: 1250,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 0.2, unit: 'pp' }
        }
      },
      {
        key: 'engagement_rate',
        label: 'Engagement Rate',
        value: 72.4,
        format: 'percent',
        delta: { value: 2.2, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'multi_source_coverage',
        label: 'Multi-Source Engagement Coverage',
        // 27,492 of the 46,915 engaged customers, which is the 58.6% the panel prints.
        value: 58.6,
        format: 'percent',
        denominator: '27,492 customers',
        delta: { value: 4.1, unit: 'pp', direction: 'up', sentiment: 'positive' }
      }
    ],
    confidence: {
      score: 83,
      band: 'high',
      reasons: [
        'Every governed source reported for the full period',
        'A customer is counted in one engagement group only'
      ]
    },
    // The screen's own body is Lorem Ipsum, so the card renders the shared placeholder rather than
    // the authored prose that used to stand here (fixtures/placeholderCopy.ts).
    whyThisResult: PLACEHOLDER_WHY_THIS_RESULT
  },

  // Node 589-30194. The card's `kpis` frame draws FOUR tiles, not the two counts that used to ship
  // here — neither `Entered the funnel` nor `Dropped before conversion` appears anywhere on it. Each
  // label and figure below is a text node of that frame: 589-30198/30199, 30201/30202, 30204/30205,
  // 30207/30208. `sentiment` is not a screen reading — no fill is exposed on the movements — so it
  // is the tooltip PDF's own reading of each metric: completion and issuance rising is improvement,
  // a smaller population entering is not.
  engagement_funnel: {
    cardKey: 'engagement_funnel',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    breakdownKey: 'engagement_funnel',
    metrics: [
      {
        key: 'funnel_completion_rate',
        label: 'Funnel Completion Rate',
        value: 28.4,
        format: 'percent',
        delta: { value: 3.2, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'customers_entering',
        label: 'Customers Entering',
        value: 48000,
        format: 'count',
        // The screen writes `↓3.2 pp` on a headcount. `pp` is kept because it is what the file
        // draws; it is on the designer's list rather than silently corrected to a count movement.
        delta: { value: 3.2, unit: 'pp', direction: 'down', sentiment: 'negative' }
      },
      {
        // The screen's movement here reads `- 00% pp` — an unfilled placeholder, not a reading — so
        // the tile ships the figure with no movement rather than a manufactured one.
        key: 'final_stage_customers',
        label: 'Final-stage Customers',
        value: 13689,
        format: 'count'
      },
      {
        key: 'outcome_issuance_rate',
        label: 'Outcome Issuance Rate',
        value: 22.8,
        format: 'percent',
        delta: { value: 1.6, unit: 'pp', direction: 'up', sentiment: 'positive' }
      }
    ],
    // 589-30387/30388 draw the badge and 589-30389 the `Reason:` line under it; the reason is still
    // Lorem Ipsum in the file, so the elements ship carrying the shared placeholder.
    confidence: { score: 83, band: 'high', reasons: [PLACEHOLDER_CONFIDENCE_REASON] }
  }
};

// The visuals the design draws around the metric row of Customer Behaviour Trends. They decorate the
// payload above rather than extending it: `IntelligenceFixture` has no field for a per-metric trend,
// a caption or an evidence table, and no endpoint shape exists yet to model one against.
export const behaviourEngagementVisuals = {
  /* One entry per metric, keyed by its `CardMetric.key` so a tile cannot pick up a neighbour's
     hue when the metric order changes. `trendHue` is the deep stop of each curve's gradient
     (589-30488 / 30484 / 30434 / 30431); all four are existing tokens, resolved to `tokens.color` by
     the section, and the hue is now ALL these entries carry. The 17-point `trend` and the
     `+8 points vs previous period` caption went with them: one authored path drawn under four
     unrelated scores, and a movement against a period the seam does not carry. The section reads
     `CardMetric.sparkline` for the curve and prints no caption. PLA-8815. */
  behaviourScoreVisuals: {
    behavioural_momentum: { trendHue: 'accent-purple' as const },
    behavioural_deviation: { trendHue: 'warning' as const },
    disengagement_early_warning: { trendHue: 'negative' as const },
    habit_strength: { trendHue: 'positive' as const }
  },

  // `TOP DISENGAGEMENT DRIVERS` (589-30438). `share` is what the row PRINTS; `barFraction` is what
  // the screen DRAWS, and the two disagree: the fills measure 125.01 / 93.76 / 69.45 of a 173.64
  // track — 72 / 54 / 40 %, a consistent x2.25 of the printed figure, identical on both frames.
  // Held apart rather than reconciled, because picking either one would invent an encoding the file
  // does not state. Designer question; the printed figure is the datum a Metabase column maps onto.
  disengagementDrivers: [
    { label: 'Recency drop', share: 0.32, barFraction: 0.72, color: 'negative' as const },
    { label: 'Frequency drop', share: 0.24, barFraction: 0.54, color: 'warning' as const },
    { label: 'Engagement drop', share: 0.18, barFraction: 0.4, color: 'accent-purple' as const }
  ],

  // `HABIT EVIDENCE` (589-30455). Categorical and unit-bearing readings, not numbers — this is the
  // evidence table beside the metric row, so the strings are the payload's, pre-formatted.
  habitEvidence: [
    { label: 'Repeated-event consistency', value: 'High' },
    { label: 'Interval variance', value: 'Low' },
    { label: 'Observed tenure', value: '14 months' }
  ],

  // Customer Engagement, node 589-30494. Every string and figure below is the screen's own; nothing
  // is derived and nothing is invented (design/card-customer-engagement-devmode-2026-08-12.md § 3).
  // Only the two definitions live here — every figure the card prints is a `CardMetric` above, so a
  // Metabase column will land on it rather than on a string in this file.
  /* The DEFINITION only. It used to open `= 46,915 distinct engaged customers ÷ 64,800 distinct
     eligible customers × 100` — two counts O-COM-011 returns no column for at all, printed beside
     the tiles that are supposed to state them. The shape of the calculation is copy; the counts were
     not. PLA-8815. */
  engagementRateFormula: {
    term: 'Engagement Rate',
    body: '= distinct engaged customers ÷ distinct eligible customers × 100'
  },
  multiSourceFormula: {
    term: 'Multi-Source Coverage',
    body: '= customers engaging across 2+ distinct sources ÷ engaged customers × 100'
  }
};
